import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SamplingHistogram from "../components/SamplingHistogram";
import { Arrow } from "../components/Layout";

const POPULATION_SIZE = 10000;
const TARGET_MEAN = 2500;
const TARGET_STD = 250;
const MIN_WEIGHT = 1700;
const MAX_WEIGHT = 3300;
const BIN_WIDTH = 100;
const SAMPLE_SIZES = [50, 300, 1000];

function seededRandom(seed) {
  let value = seed >>> 0;
  return () => {
    value = (1664525 * value + 1013904223) >>> 0;
    return value / 4294967296;
  };
}

function normalRandom(random) {
  const u1 = Math.max(random(), Number.EPSILON);
  const u2 = random();
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}

function makePopulation() {
  const random = seededRandom(20260829);
  const raw = Array.from({ length: POPULATION_SIZE }, () => TARGET_MEAN + TARGET_STD * normalRandom(random));
  const rawMean = raw.reduce((sum, value) => sum + value, 0) / raw.length;
  const rawVariance = raw.reduce((sum, value) => sum + ((value - rawMean) ** 2), 0) / raw.length;
  const rawStd = Math.sqrt(rawVariance);
  return raw.map((value) => TARGET_MEAN + ((value - rawMean) / rawStd) * TARGET_STD);
}

function getBins() {
  const bins = [];
  for (let start = MIN_WEIGHT; start < MAX_WEIGHT; start += BIN_WIDTH) bins.push(start);
  return bins;
}

function histogram(values, bins) {
  const counts = new Array(bins.length).fill(0);
  values.forEach((value) => {
    const index = Math.floor((value - MIN_WEIGHT) / BIN_WIDTH);
    if (index >= 0 && index < counts.length) counts[index] += 1;
  });
  return counts.map((count) => (count / values.length) * 100);
}

function mean(values) {
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function drawSample(population, n, seed) {
  const random = seededRandom(seed);
  return Array.from({ length: n }, () => population[Math.floor(random() * population.length)]);
}

export default function SimulationPage() {
  const [run, setRun] = useState(1);
  const population = useMemo(() => makePopulation(), []);
  const bins = useMemo(() => getBins(), []);
  const truthHistogram = useMemo(() => histogram(population, bins), [population, bins]);
  const truthMean = useMemo(() => mean(population), [population]);

  const samples = useMemo(() => SAMPLE_SIZES.map((n, index) => {
    const values = drawSample(population, n, 9001 + run * 7919 + index * 104729);
    return {
      n,
      values,
      distribution: histogram(values, bins),
      sampleMean: mean(values),
      standardError: TARGET_STD / Math.sqrt(n),
    };
  }), [population, bins, run]);

  const yMax = Math.max(...truthHistogram, ...samples.flatMap((sample) => sample.distribution)) * 1.12;

  return (
    <div className="page page-simulation">
      <section className="page-hero page-section simulation-hero">
        <div>
          <p className="eyebrow">03 / Interactive simulation</p>
          <h1>Perfect measurement can still leave sampling uncertainty.</h1>
        </div>
        <div className="page-hero-lead">
          <p>Every sampled bird below is measured with <strong>100% accuracy</strong>. No camera error. No scale error. No manual error.</p>
          <p>Only one thing changes: how many IID observations we draw from the same flock.</p>
        </div>
      </section>

      <section className="page-section simulation-controls">
        <div className="truth-strip">
          <div><span>Population</span><strong>{POPULATION_SIZE.toLocaleString()} birds</strong></div>
          <div><span>True mean</span><strong>{truthMean.toFixed(0)} g</strong></div>
          <div><span>True standard deviation</span><strong>{TARGET_STD} g</strong></div>
          <div><span>Individual measurement error</span><strong>0%</strong></div>
        </div>
        <button className="resample-button" type="button" onClick={() => setRun((value) => value + 1)}>
          Draw another IID sample <motion.span key={run} initial={{ rotate: -120, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }}>↻</motion.span>
        </button>
      </section>

      <section className="page-section histogram-section">
        <div className="histogram-explainer">
          <div>
            <span className="legend truth-legend">Population reference</span>
            <span className="legend sample-legend">Observed sample</span>
          </div>
          <p>All three charts use the <strong>same bin edges, same x-axis and same y-scale</strong>. The vertical marker is the mean of that random sample. Resample repeatedly and watch what changes.</p>
        </div>

        <div className="histogram-grid">
          {samples.map((sample, index) => (
            <SamplingHistogram
              key={`${sample.n}-${run}`}
              title={index === 0 ? "Small sample" : index === 1 ? "Operational sample" : "Large sample"}
              n={sample.n}
              bins={bins}
              sampleValues={sample.distribution}
              truthValues={truthHistogram}
              yMax={yMax}
              sampleMean={sample.sampleMean}
              truthMean={truthMean}
              min={MIN_WEIGHT}
              max={MAX_WEIGHT}
              standardError={sample.standardError}
            />
          ))}
        </div>
      </section>

      <section className="page-section simulation-reading">
        <div className="section-label">What you are seeing</div>
        <div className="reading-grid">
          <article>
            <span>01</span>
            <h3>The bird can be measured perfectly.</h3>
            <p>That does not make a small sample representative. The uncertainty exists before we add any model error.</p>
          </article>
          <article>
            <span>02</span>
            <h3>The mean becomes more stable as n grows.</h3>
            <p>The expected standard error scales approximately with σ / √n. Going from 50 to 1,000 observations changes the sampling problem dramatically.</p>
          </article>
          <article>
            <span>03</span>
            <h3>The distribution matters too.</h3>
            <p>A flock is not only an average. Larger samples recover the shape, tails and heterogeneity of the population more reliably.</p>
          </article>
        </div>
      </section>

      <section className="page-section camera-scale-section">
        <div>
          <p className="eyebrow">Why cameras enter the story</p>
          <h2>At large repeated sample sizes, the operational constraint changes.</h2>
        </div>
        <div>
          <p>Manual weighing can provide excellent individual truth. Automated scales can also provide highly reliable individual measurements. But neither is designed to make repeated IID-like samples in the hundreds or thousands routine across the flock.</p>
          <p>Among these three approaches, camera-based observation is the practical route to making very large repeated samples operational without handling animals one by one.</p>
          <p className="muted-copy">That does not remove camera error. It changes the error budget—and gives us enough observations to work on sampling, calibration and drift in a different way.</p>
        </div>
      </section>

      <section className="page-section model-error-section">
        <div className="three-percent">≈3%</div>
        <div>
          <h2>Our calibrated individual model error can be around 3%. That is not the headline.</h2>
          <p>A strong individual model is necessary, but a deployed measurement system must also remain representative as genetics, environments and management conditions change. The interesting work starts after the model score.</p>
          <Link className="text-link" to="/approach">Deployment, drift and calibration <Arrow /></Link>
        </div>
      </section>

      <section className="page-section next-page-card">
        <span>Next / 04</span>
        <div>
          <h2>A model can stay unchanged while the farm changes around it.</h2>
          <p>See why we focus on calibration and error transparency.</p>
        </div>
        <Link className="round-link" to="/approach" aria-label="Go to approach">→</Link>
      </section>
    </div>
  );
}
